// Vertrag der Listenköpfe (Vorarbeit zu Runde 5, 14.10.2026). Hier stehen nur Typen und Schlüssel, keine Logik.
// Er verbindet vier Agenten, die getrennt bauen:
//   5A baut `FilterZeile` (Bedingungen als Pillen, Editor, Speichern) und liefert über `onEntwurf` einen `FilterEntwurf`,
//   5B baut `Bedienleiste` (Zähler, Sortierung, Spalten, Auswahl, Textsuche) und merkt die Wahl je Nutzer,
//   5C und 5D setzen `Bedienleiste` auf ihren Seiten (Kontakte, Aktivitäten, Opportunities) ein.
// Diese Datei ändert in Runde 5 niemand. Fehlt etwas, wird eine Eigenschaft ADDITIV und OPTIONAL ergänzt und in
// AENDERUNGSWUENSCHE.md eingetragen; bestehende Namen und Formen bleiben (ZUSTAENDIGKEIT.md, Abschnitt "Runde 5").
import type { ReactNode } from "react";
import type { FilterEntwurf } from "../../api/filter";
import type { SmartView, SmartViewKurz } from "../../api/types";

export type { FilterEntwurf };

/** Schlüssel der Listen für die gemerkte Ansicht (`listen_ansichten.liste`, API.md 20.2). */
export const ListenSchluessel = {
  leads: "leads",
  smartView: (viewId: string) => `smartview:${viewId}`,
  kontakte: "kontakte",
  /** art: anrufe | emails | sms | whatsapp | meetings | notizen | custom (bei custom: `custom:<typ_id>`) */
  aktivitaeten: (art: string) => `aktivitaeten:${art}`,
  opportunities: "opportunities",
  opportunitiesBoard: "opportunities:board",
} as const;

/** Spalte, die zur Wahl steht (Spaltenwahl, Sortierwahl). */
export interface ListenSpalte {
  key: string;
  label: string;
  /** Überschrift in der Wahl (z. B. "Anrufe", "Lead-Felder") */
  gruppe?: string;
  sortierbar?: boolean;
  /** lässt sich nicht abwählen (z. B. der Name) */
  fest?: boolean;
}

export interface ListenSortierung {
  key: string;
  ab: boolean;
}

/** Eigenschaften der Bedienleiste. Der Platzhalter der Vorarbeit zeigt nur Zähler, `vorn`, `hinweis`, `warnung` und `children`;
 *  die übrigen baut 5B. Die Seiten müssen sie schon jetzt übergeben können, ohne dass sich der Aufruf später ändert. */
export interface BedienleisteProps {
  /** Schlüssel der Liste (`ListenSchluessel`), unter dem 5B die Wahl des Nutzers merkt */
  liste: string;
  /** Wort für die Zahl: { eins: "Lead", viele: "Leads" } */
  einheit: { eins: string; viele: string };
  /** Treffer insgesamt; null = unbekannt */
  trefferZahl: number | null;
  /** schon geladene Zeilen; kleiner als die Trefferzahl → "· n geladen" */
  geladen?: number;
  laedt?: boolean;
  /** Text, solange noch kein Ergebnis da ist (Smart View: "Treffer werden von Close geholt …") */
  ladeText?: string;
  /** Spaltenwahl: alle wählbaren Spalten, die gezeigten Schlüssel in Reihenfolge, Rückmeldung (null = Standard der Liste) */
  spalten?: ListenSpalte[];
  gezeigt?: string[];
  onSpalten?: (keys: string[] | null) => void;
  /** Sortierung: aktuelle Wahl (null = Reihenfolge der Quelle), wählbare Felder (Standard: `spalten` mit sortierbar), Rückmeldung */
  sort?: ListenSortierung | null;
  sortFelder?: ListenSpalte[];
  onSort?: (s: ListenSortierung | null) => void;
  /** Textsuche in der Liste */
  suche?: { wert: string; onAendern: (wert: string) => void; platzhalter?: string };
  /** Auswahl (Kästchen, Taste x): Zahl der Markierten, alle geladenen markieren, Markierung aufheben */
  auswahl?: { anzahl: number; onAlleMarkieren?: () => void; onAufheben: () => void };
  /** Platz vor der Zahl (Leads-Liste: Suchfeld und Statusfilter stehen dort) */
  vorn?: ReactNode;
  /** Hinweis neben der Zahl, z. B. "Sortiert nach …" */
  hinweis?: ReactNode;
  /** Warnung neben der Zahl, z. B. "n Treffer fehlen noch in der lokalen Kopie" */
  warnung?: ReactNode;
  /** Platz rechts neben den Standardelementen */
  children?: ReactNode;
}

/** Eigenschaften der Filterzeile (zwischen Listenkopf und Bedienleiste). 5A baut sie; bis dahin zeigt der Platzhalter nichts. */
export interface FilterZeileProps {
  /** Wo die Zeile steht: auf einer gespeicherten Smart View oder auf der Leads-Liste */
  art: "smartview" | "leads";
  /** gespeicherte Smart View (leer auf der Leads-Liste) */
  viewId?: string | null;
  view?: SmartView | null;
  /** Treffer der Liste in der gerade gezeigten Abfrage */
  trefferZahl: number | null;
  /** Stand des Filters, den der Nutzer gerade bearbeitet; null = wie gespeichert. Hält die Seite einen Entwurf, laden Treffer
   *  und Zähler aus `filterApi.treffer` (die Seite macht das, 5A muss nur den Entwurf setzen oder löschen). */
  entwurf: FilterEntwurf | null;
  onEntwurf: (entwurf: FilterEntwurf | null) => void;
  /** Nach "Save" oder "Save as": die Seite lädt neu bzw. wechselt zur neuen Smart View (`/smart-views/<id>`) */
  onGespeichert?: (view: SmartViewKurz) => void;
}
